"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import apiClient from "../utils/apiClient"

const DefaultSlotManagement = () => {
  const navigate = useNavigate()
  const [slots, setSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [templateType, setTemplateType] = useState("default")
  const [editDialog, setEditDialog] = useState({
    open: false,
    slot: null,
    isNew: false,
  })

  const PREDEFINED_SLOTS = [
    {
      value: "Breakfast",
      label: "Breakfast",
      startTime: templateType === "saturday" ? "09:00" : "08:00",
      endTime: templateType === "saturday" ? "09:30" : "08:40",
    },
    {
      value: "Lunch",
      label: "Lunch",
      startTime: templateType === "saturday" ? "12:00" : "11:00",
      endTime: templateType === "saturday" ? "12:30" : "11:40",
    },
    {
      value: "Dinner",
      label: "Dinner",
      startTime: "19:00",
      endTime: "19:40",
    },
  ]

  const getPredefinedSlotTimes = (slotName) => {
    const slot = PREDEFINED_SLOTS.find((slot) => slot.value === slotName)
    return slot ? { startTime: slot.startTime, endTime: slot.endTime } : null
  }

  const fetchSlots = useCallback(async (type) => {
    setLoading(true)
    try {
      const response = await apiClient.get(`/admin/slots?type=${type}`)
      const slotData = response.data.slot

      if (slotData && slotData.slots) {
        console.log('Raw slot data:', slotData)
        const formattedSlots = slotData.slots.map((slot) => {
          console.log('Mapping slot:', slot)
          return {
            id: slot.id,
            name: slot.name,
            startTime: slot.startTime,
            endTime: slot.endTime,
            restaurants: slot.restaurants || [],
            foodItems: slot.foodItems || [],
            item_options: slot.item_options || [],
          }
        })
        console.log('Formatted slots:', formattedSlots)
        setSlots(formattedSlots)
      } else {
        console.log('No slot data or slots array')
        setSlots([])
      }
    } catch (error) {
      console.error("Error fetching slots:", error)
      toast.error(`Failed to fetch ${type} slots`)
      setSlots([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSlots(templateType)
  }, [fetchSlots, templateType])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchSlots(templateType)
    setTimeout(() => setRefreshing(false), 500)
  }

  const handleSaveSlots = async () => {
    setSaving(true)
    try {
      const formattedSlots = slots.map((slot) => ({
        id: slot.id,
        name: slot.name,
        startTime: slot.startTime,
        endTime: slot.endTime,
        restaurants: slot.restaurants,
        foodItems: slot.foodItems || [],
        item_options: slot.item_options || [],
      }))

      const requestBody = {
        slots: formattedSlots,
        is_active: true,
        slot_type: templateType
      }

      await apiClient.patch(`/admin/slots`, requestBody)
      toast.success("Template slots saved successfully!")
      await fetchSlots(templateType)
    } catch (error) {
      console.error("Error saving slots:", error)
      toast.error("Failed to save slots")
    } finally {
      setSaving(false)
    }
  }

  const handleAddSlot = () => {
    setEditDialog({
      open: true,
      slot: {
        id: Date.now(),
        name: "",
        startTime: "",
        endTime: "",
        restaurants: [],
        foodItems: [],
        item_options: [],
        isActive: true,
      },
      isNew: true,
    })
  }

  const handleEditSlot = (slot) => {
    setEditDialog({
      open: true,
      slot: { ...slot },
      isNew: false,
    })
  }

  const handleDeleteSlot = (slotId) => {
    setSlots(slots.filter((slot) => slot.id !== slotId))
    toast.success("Slot deleted. Click Save Changes to apply.")
  }

  const handleSaveSlot = () => {
    const { slot, isNew } = editDialog

    if (!slot.name || !slot.startTime || !slot.endTime) {
      toast.error("Please fill in slot name, start time, and end time")
      return
    }

    if (!slot.item_options || slot.item_options.length === 0) {
      toast.error("Please add at least one item option")
      return
    }

    const updatedSlot = {
      ...slot,
      foodItems: slot.foodItems || [],
      item_options: slot.item_options || [],
    }

    if (isNew) {
      setSlots([...slots, updatedSlot])
      toast.success("New template slot added. Click Save Changes to apply.")
    } else {
      setSlots(slots.map((s) => (s.id === updatedSlot.id ? updatedSlot : s)))
      toast.success("Template slot updated. Click Save Changes to apply.")
    }

    setEditDialog({ open: false, slot: null, isNew: false })
  }

  const handleAddItemOption = (option) => {
    const currentOptions = editDialog.slot?.item_options || []
    if (!currentOptions.includes(option)) {
      setEditDialog({
        ...editDialog,
        slot: {
          ...editDialog.slot,
          item_options: [...currentOptions, option],
        },
      })
    }
  }

  const handleRemoveItemOption = (optionToRemove) => {
    const currentOptions = editDialog.slot?.item_options || []
    setEditDialog({
      ...editDialog,
      slot: {
        ...editDialog.slot,
        item_options: currentOptions.filter((option) => option !== optionToRemove),
      },
    })
  }

  const handleAddCustomOption = (customOption) => {
    if (customOption.trim()) {
      handleAddItemOption(customOption.trim())
    }
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress size={60} sx={{ color: "#20d5d5" }} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ pb: { xs: 10, sm: 4 } }}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            p: 4,
            background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
            borderRadius: 4,
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <ScheduleIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h3" fontWeight={800}>
                  Default Slot Templates
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Manage template slots for different days
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh Slots">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{ color: "white" }}
              >
                <motion.div animate={refreshing ? { rotate: 360 } : {}}>
                  <RefreshIcon />
                </motion.div>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Controls */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/slot-management')}
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                borderColor: "#20d5d5",
                color: "#20d5d5",
              }}
            >
              Today's Slots
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                color: "white",
              }}
            >
              Templates
            </Button>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
                label="Template Type"
              >
                <MenuItem value="default">Monday-Friday</MenuItem>
                <MenuItem value="saturday">Saturday</MenuItem>
                <MenuItem value="sunday">Sunday</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSlot}
              sx={{ background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)" }}
            >
              Add Template Slot
            </Button>

            <Box sx={{ ml: "auto" }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveSlots}
                disabled={saving}
                sx={{ background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)" }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Slots Display */}
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          {templateType === "default" ? "Monday-Friday" : templateType === "saturday" ? "Saturday" : "Sunday"} Template Slots
        </Typography>

        {slots.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: "center", borderRadius: 4 }}>
            <ScheduleIcon sx={{ fontSize: 64, color: "#64748b", mb: 2 }} />
            <Typography variant="h4" fontWeight={800} gutterBottom>
              No Template Slots
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Add template slots for this configuration
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSlot}
              sx={{ background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)" }}
            >
              Add First Template Slot
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {slots.map((slot) => (
              <Grid item xs={12} md={6} lg={4} key={slot.id}>
                <Card sx={{ height: "100%" }}>
                  <Box
                    sx={{
                      background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                      p: 2,
                      color: "white",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="h6" fontWeight={600}>
                        {slot.name}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSlot(slot)}
                          sx={{ color: "white" }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSlot(slot.id)}
                          sx={{ color: "#f87171" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 2, p: 2, borderRadius: 2, background: "#e3f2fd" }}>
                      <Typography variant="h6" fontWeight={700} color="#1976d2">
                        {slot.startTime} – {slot.endTime}
                      </Typography>
                    </Box>

                    {/* Restaurants */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                        Available Restaurants:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {slot.restaurants && slot.restaurants.length > 0 ? (
                          slot.restaurants.map((restaurant, idx) => (
                            <Chip
                              key={idx}
                              label={restaurant}
                              size="small"
                              sx={{
                                background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          ))
                        ) : (
                          <Chip
                            label="No specific restaurants"
                            size="small"
                            sx={{
                              background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                              color: "#64748b",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Food Items */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                        Food Items:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {slot.foodItems && slot.foodItems.length > 0 ? (
                          slot.foodItems.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={item}
                              size="small"
                              sx={{
                                background: "linear-gradient(135deg, #ff9800 0%, #ff5722 100%)",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          ))
                        ) : (
                          <Chip
                            label="No specific food items"
                            size="small"
                            sx={{
                              background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                              color: "#64748b",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Item Options */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                        Item Options:
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {slot.item_options && slot.item_options.length > 0 ? (
                          slot.item_options.map((option, idx) => (
                            <Chip
                              key={idx}
                              label={option}
                              size="small"
                              sx={{
                                background: "linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)",
                                color: "white",
                                fontWeight: 600,
                              }}
                            />
                          ))
                        ) : (
                          <Chip
                            label="No item options"
                            size="small"
                            sx={{
                              background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                              color: "#64748b",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label="Template"
                        size="small"
                        sx={{ background: "#2e7d32", color: "white" }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Edit Dialog */}
        <Dialog
          open={editDialog.open}
          onClose={() => setEditDialog({ open: false, slot: null, isNew: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, background: "#2c3e50", color: "white" }}>
            {editDialog.isNew ? "Add New Template Slot" : "Edit Template Slot"}
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Slot Name</InputLabel>
                  <Select
                    value={editDialog.slot?.name || ""}
                    onChange={(e) => {
                      const selectedSlot = PREDEFINED_SLOTS.find(s => s.value === e.target.value)
                      setEditDialog({
                        ...editDialog,
                        slot: {
                          ...editDialog.slot,
                          name: e.target.value,
                          startTime: selectedSlot?.startTime || "",
                          endTime: selectedSlot?.endTime || "",
                        },
                      })
                    }}
                    label="Slot Name"
                  >
                    {PREDEFINED_SLOTS.map((slot) => (
                      <MenuItem key={slot.value} value={slot.value}>
                        {slot.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={editDialog.slot?.startTime || ""}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={editDialog.slot?.endTime || ""}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Restaurants (optional, comma separated)"
                  value={editDialog.slot?.restaurantText || editDialog.slot?.restaurants?.join(", ") || ""}
                  onChange={(e) => {
                    const restaurantText = e.target.value
                    setEditDialog({
                      ...editDialog,
                      slot: {
                        ...editDialog.slot,
                        restaurantText: restaurantText,
                        restaurants: restaurantText
                          .split(",")
                          .map((r) => r.trim())
                          .filter((r) => r.length > 0)
                      },
                    })
                  }}
                  placeholder="e.g., Resto A, Resto B, Resto C (optional)"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Item Options (at least one required):
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {editDialog.slot?.item_options?.map((option, idx) => (
                      <Chip
                        key={idx}
                        label={option}
                        onDelete={() => handleRemoveItemOption(option)}
                        sx={{ background: "#673ab7", color: "white" }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    placeholder="Custom option"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddCustomOption(e.target.value)
                        e.target.value = ""
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      const input = e.target.closest(".MuiBox-root").querySelector("input")
                      handleAddCustomOption(input.value)
                      input.value = ""
                    }}
                  >
                    Add
                  </Button>
                </Box>
                
                {/* Veg/Non-Veg Toggle */}
                {editDialog.slot?.item_options?.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2, background: "#f9f9f9" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Veg/Non-Veg Options:
                    </Typography>
                    {(() => {
                      // Get all unique base options (both single and from veg/non-veg variants)
                      const allBaseOptions = new Set()
                      
                      editDialog.slot.item_options.forEach(option => {
                        if (option.includes("(Veg)")) {
                          allBaseOptions.add(option.replace(" (Veg)", ""))
                        } else if (option.includes("(Non-Veg)")) {
                          allBaseOptions.add(option.replace(" (Non-Veg)", ""))
                        } else {
                          allBaseOptions.add(option)
                        }
                      })
                      
                      return Array.from(allBaseOptions).map((baseOption) => {
                        const hasVegNonVeg = editDialog.slot?.item_options?.includes(`${baseOption} (Veg)`) || editDialog.slot?.item_options?.includes(`${baseOption} (Non-Veg)`)
                        return (
                          <Box key={baseOption} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                            <Typography variant="body2">{baseOption}</Typography>
                            <Button
                              size="small"
                              variant={hasVegNonVeg ? "contained" : "outlined"}
                              onClick={() => {
                                const currentOptions = editDialog.slot?.item_options || []
                                if (hasVegNonVeg) {
                                  // Remove veg/non-veg variants and add back base option
                                  const newOptions = currentOptions.filter(opt => 
                                    opt !== `${baseOption} (Veg)` && opt !== `${baseOption} (Non-Veg)`
                                  )
                                  if (!newOptions.includes(baseOption)) {
                                    newOptions.push(baseOption)
                                  }
                                  setEditDialog({
                                    ...editDialog,
                                    slot: { ...editDialog.slot, item_options: newOptions }
                                  })
                                } else {
                                  // Remove base option and add veg/non-veg variants
                                  const newOptions = currentOptions.filter(opt => opt !== baseOption)
                                  newOptions.push(`${baseOption} (Veg)`, `${baseOption} (Non-Veg)`)
                                  setEditDialog({
                                    ...editDialog,
                                    slot: { ...editDialog.slot, item_options: newOptions }
                                  })
                                }
                              }}
                              sx={{ minWidth: 80, fontSize: "11px" }}
                            >
                              {hasVegNonVeg ? "Veg/Non-Veg" : "Single"}
                            </Button>
                          </Box>
                        )
                      })
                    })()}
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setEditDialog({ open: false, slot: null, isNew: false })}>
              Cancel
            </Button>
            <Button onClick={handleSaveSlot} variant="contained">
              {editDialog.isNew ? "Add Slot" : "Update Slot"}
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Container>
  )
}

export default DefaultSlotManagement